import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { ENTITY_TYPES } from '@sales-center/common'
import type { ILeadDetail } from '@user/store/lead/typings'
import type { IOpportunityDetail } from '@user/store/opportunity/typings'
import { CardVariants } from '@user/components/Contacts/ContactCard'
import { ContactsDialog } from '@user/components/Contacts/ContactsDialog'
import { UpdateContactDrawer } from '@user/components/Contacts/UpdateContact/UpdateContactDrawer'
import { useCleanupEntityContacts } from '@user/components/Contacts/hooks/useCleanupEntityContacts'

import { TabPanel } from '@sales-center/components'
import { useActions } from '../../../Contacts/ContactsPage/hooks'
import {
  EntityDetailsContactCard,
  EntityDetailsContactList,
  TabsStyled,
  TabStyled,
} from '@user/features/EntityDetails/components'
import { RemoveContact } from '@user/components/Contacts/RemoveContact/RemoveContact'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'

interface Props {
  detail: ILeadDetail | IOpportunityDetail
  entityId: number
  entityType: ENTITY_TYPES
  canUpdateEntity: boolean
  addContactOpen: boolean
  onCloseAddContact: () => void
  onOpenAddContact: (event: React.MouseEvent) => void
}

const styles = {
  fullHeight: {
    height: '100%',
  },
}

export function Contacts(props: Props) {
  const {
    detail,
    entityId,
    entityType,
    canUpdateEntity,
    addContactOpen,
    onCloseAddContact,
    onOpenAddContact,
  } = props

  //========== S T A T E ==========
  const [secondaryContactsActiveTab, setSecondaryContactsActiveTab] = useState<
    number | undefined
  >()
  const [showSecondaryContacts, setShowSecondaryContacts] =
    useState<boolean>(false)

  //========== H O O K S ==========
  const { extContacts, wwmContacts, primary } = useCleanupEntityContacts(detail)
  const wwmContactsCount = wwmContacts.length

  useEffect(() => {
    setSecondaryContactsActiveTab(wwmContactsCount > 0 ? 1 : 2)
  }, [wwmContactsCount])

  //========== E V E N T S ==========
  const handleCloseSecondaryContacts = useCallback(() => {
    setShowSecondaryContacts(false)
  }, [])

  const [tabIndex, setTabIndex] = useState(0)

  const handleChange = useCallback(
    (_, newTabIndex: number) => setTabIndex(newTabIndex),
    [],
  )

  const {
    onCloseDelete,
    deleteContactId,
    deleteContactOpen,

    updateContactId,
    showUpdateContact,
    onCloseUpdateContact,
    updateContactIsPrimary,

    getMenuItems,
  } = useActions(props, wwmContacts.length, detail)

  const theme = useTheme()

  const setColor = useCallback(
    (index: number) =>
      index === tabIndex ? '#19305A' : theme.palette.text.secondary,
    [tabIndex, theme.palette.text.secondary],
  )

  const primaryContactVariant = detail.primaryContact.externalLinkId
    ? CardVariants.pestpac
    : CardVariants.wwm

  const primaryContactMenuOptions = useMemo(
    () => (primary ? getMenuItems(primary, primaryContactVariant) : undefined),
    [getMenuItems, primary, primaryContactVariant],
  )
  return (
    <Stack>
      <Stack
        p={2}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h6" color={theme.palette.text.primary}>
          Contacts
        </Typography>
        {!!canUpdateEntity && (
          <Button
            onClick={onOpenAddContact}
            size="medium"
            variant="outlined"
            data-test-id="addContactBtn"
          >
            <Typography color="#19305A" fontFamily="Source Sans Pro Semibold">
              Add Contact{' '}
            </Typography>
          </Button>
        )}
      </Stack>
      <TabsStyled
        indicatorColor="primary"
        onChange={handleChange}
        value={tabIndex}
      >
        <TabStyled
          label="Primary Contact"
          color={setColor(0)}
          data-test-id="primaryContactTab"
        />
        <TabStyled
          label="WorkWave Marketing Contacts"
          color={setColor(1)}
          data-test-id="workWaveMarketingContactsTab"
        />
        <TabStyled
          label="PestPac Contacts"
          color={setColor(2)}
          data-test-id="pestPacContactsTab"
        />
      </TabsStyled>
      <TabPanel value={0} tab={tabIndex}>
        <Stack p={2}>
          <Typography
            variant="subtitle1"
            mb={2}
            color={theme.palette.text.primary}
          >
            Primary Contact
          </Typography>
          <EntityDetailsContactCard
            {...props}
            isPrimaryContact
            hidePrimaryRadio
            sx={styles.fullHeight}
            contact={detail.primaryContact}
            variant={primaryContactVariant}
            menuOptions={primaryContactMenuOptions}
          />
        </Stack>
      </TabPanel>
      <TabPanel value={1} tab={tabIndex}>
        <Box m={2}>
          <Typography
            variant="subtitle1"
            mb={2}
            color={theme.palette.text.primary}
          >
            WorkWave Marketing Contacts
          </Typography>
          <Typography variant="body2" color={theme.palette.text.primary}>
            WorkWave Marketing Contacts can be created and managed from here.
            They can also be pushed to PestPac if the lead or opportunity is
            linked to a location. Best Contact Method and Best Contact Time do
            not transfer into PestPac.
          </Typography>
        </Box>

        <EntityDetailsContactList
          {...props}
          items={wwmContacts}
          variant={CardVariants.wwm}
          getMenuItems={getMenuItems}
          primaryContactId={detail!.primaryContact.id}
          primaryContact={detail!.primaryContact}
        />
      </TabPanel>
      <TabPanel value={2} tab={tabIndex}>
        <Box m={2}>
          <Typography
            variant="subtitle1"
            mb={2}
            color={theme.palette.text.primary}
          >
            PestPac Contacts
          </Typography>
          <Typography variant="body2" color={theme.palette.text.primary}>
            PestPac Contacts are displayed here when a lead or opportunity is
            linked to a location. They can be edited in PestPac.
          </Typography>
        </Box>
        <EntityDetailsContactList
          {...props}
          items={extContacts}
          variant={CardVariants.pestpac}
          getMenuItems={getMenuItems}
          primaryContactId={detail!.primaryContact.id}
          primaryContact={detail!.primaryContact}
        />
      </TabPanel>
      <ContactsDialog
        canUpdateEntity={canUpdateEntity}
        entityId={entityId}
        entityType={entityType}
        initialTab={secondaryContactsActiveTab}
        open={showSecondaryContacts}
        onClose={handleCloseSecondaryContacts}
      />
      <UpdateContactDrawer
        entityType={entityType}
        onClose={onCloseAddContact}
        open={addContactOpen}
        entityId={entityId}
        detail={detail}
      />
      {/* ========== D E L E T E   C O N T A C T ========== */}
      <RemoveContact
        {...props}
        onClose={onCloseDelete}
        open={deleteContactOpen}
        contactId={deleteContactId}
      />
      {/* ========== U P D A T E   C O N T A C T ========== */}
      {!!detail && (
        <UpdateContactDrawer
          detail={detail}
          entityId={entityId}
          entityType={entityType}
          open={showUpdateContact}
          contactId={updateContactId}
          onClose={onCloseUpdateContact}
          isPrimaryContact={updateContactIsPrimary}
        />
      )}
    </Stack>
  )
}
